import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SystemDetails } from '@twaice-fe/shared/models';
import { selectors } from '@twaice-fe/frontend/shared/store';
import mixpanel from 'mixpanel-browser';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { SystemChatComponent } from './chat-component/chat.component';

const { systemSelectors } = selectors;

@Injectable({
  providedIn: 'root',
})
export class ChatDrawerService {
  private static readonly OPEN_PARAM = 'notesPanel';
  private static readonly HIGHLIGHT_PARAM = 'note';

  selectedSystem: SystemDetails;
  private autoOpenHandled = false;

  constructor(
    private drawerService: NzDrawerService,
    private store: Store,
    private router: Router
  ) {
    this.store
      .select(systemSelectors.getSelected)
      .pipe(takeUntilDestroyed())
      .subscribe((selectedSystem) => {
        this.selectedSystem = selectedSystem;
        // If we arrived via a note's "go to page" deep link, open the notes drawer once the
        // target system is known. Guarded so a refresh (params stripped) won't reopen it.
        if (selectedSystem?.customerBk && selectedSystem?.systemBk) {
          this.openNotesFromUrl(selectedSystem);
        }
      });
  }

  static notesDeepLinkParams(noteTimestamp: string): Record<string, string> {
    return {
      [ChatDrawerService.OPEN_PARAM]: 'open',
      [ChatDrawerService.HIGHLIGHT_PARAM]: noteTimestamp,
    };
  }

  /**
   * Opens the view a note was created on in a new tab, carrying deep-link params so that page
   * auto-opens the notes panel and scrolls to this note. No-op when the note has no source view.
   */
  openNoteSourceView(sourceUrl: string | null, noteTimestamp: string): void {
    if (!sourceUrl) return;

    const urlTree = this.router.parseUrl(sourceUrl);
    urlTree.queryParams = { ...urlTree.queryParams, ...ChatDrawerService.notesDeepLinkParams(noteTimestamp) };
    const url = this.router.serializeUrl(urlTree);

    mixpanel.track('Note Link Followed', { sourceUrl: `${location.origin}${url}` });
    window.open(url, '_blank');
  }

  openChatDrawer({
    customerBk,
    systemBk,
    defaultTags,
    tagFilter,
    systemName,
    scrollToNoteId,
  }: {
    customerBk?: string;
    systemBk?: string;
    defaultTags?: string[];
    tagFilter?: string[];
    systemName?: string;
    scrollToNoteId?: string;
  }) {
    this.drawerService.create({
      nzContent: SystemChatComponent,
      nzClosable: true,
      nzPlacement: 'right',
      nzSize: 'large',
      nzTitle: `Notes for ${systemName || this.selectedSystem.systemName}`,
      nzData: {
        customerBk: customerBk || this.selectedSystem.customerBk,
        systemBk: systemBk || this.selectedSystem.systemBk,
        defaultTags,
        tagFilter,
        scrollToNoteId,
      },
      nzBodyStyle: { padding: 0 },
    });

    mixpanel.track('Notes Opened');
  }

  private openNotesFromUrl(system: Pick<SystemDetails, 'customerBk' | 'systemBk'>): void {
    if (this.autoOpenHandled) return;

    const params = new URLSearchParams(location.search);
    if (params.get(ChatDrawerService.OPEN_PARAM) !== 'open') return;

    this.autoOpenHandled = true;
    const scrollToNoteId = params.get(ChatDrawerService.HIGHLIGHT_PARAM) ?? undefined;

    this.openChatDrawer({ customerBk: system.customerBk, systemBk: system.systemBk, scrollToNoteId });

    const url = new URL(location.href);
    url.searchParams.delete(ChatDrawerService.OPEN_PARAM);
    url.searchParams.delete(ChatDrawerService.HIGHLIGHT_PARAM);
    history.replaceState(history.state, '', url.toString());
  }
}
